/**
 * Copyright (c) 2018 Mathis Zeiher
 *
 * Permission to use, copy, modify, and distribute this software for any
 * purpose with or without fee is hereby granted, provided that the above
 * copyright notice and this permission notice appear in all copies.
 *
 * THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES
 * WITH REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF
 * MERCHANTABILITY AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR
 * ANY SPECIAL, DIRECT, INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES
 * WHATSOEVER RESULTING FROM LOSS OF USE, DATA OR PROFITS, WHETHER IN AN
 * ACTION OF CONTRACT, NEGLIGENCE OR OTHER TORTIOUS ACTION, ARISING OUT OF
 * OR IN CONNECTION WITH THE USE OR PERFORMANCE OF THIS SOFTWARE.
 */

/* global window, CSSStyleSheet, console */
import { CustomElement } from '../customelement.js';
import { setComponentProperties, getComponentProperties } from '../componentproperties.js';
import { supportsAdoptingStyleSheets } from '../utils.js';
/**
 * stage-2 implementation of the component decorator
 *
 * @param { ComponentOptions } options - ComponentOptions
 * @returns { Stage2ClassDecorator<typeof CustomElement> } class decorator
 */

export function Component(options) {
  return descriptor => {
    return {
      elements: descriptor.elements,
      constructor: undefined,
      kind: 'class',
      finisher: target => {
        if (!CustomElement.isPrototypeOf(target)) {
          throw new Error(`${target.name} must extend CustomElement`);
        }

        const isAbstract = options.abstract;

        if (isAbstract) {
          options.tag = target.name;
        }

        const componentExtendedOptions = { ...options,
          cssStyles: [],
          shadyCSSStyleSheetAdopted: false,
          templateCache: null,
          dependenciesLoaded: false,
          dependenciesLoading: false
        };
        const componentStyles = Array.isArray(options.style) ? options.style : [options.style || ''];
        const componentCSSStyles = componentStyles.map(value => {
          if (supportsAdoptingStyleSheets) {
            if (typeof value === 'string') {
              const css = new CSSStyleSheet();
              css.replaceSync(value);
              return css;
            } else {
              return value;
            }
          } else {
            return {
              cssText: typeof value === 'string' ? value : Array.from(value.cssRules).reduce((prev, current) => {
                return prev + current.cssText;
              }, ''),
              cssRules: null
            };
          }
        });

        if (componentExtendedOptions.inheritStyle) {
          const parentStyle = getComponentProperties(Object.getPrototypeOf(target)).cssStyles;
          componentExtendedOptions.cssStyles.push(...parentStyle);
          componentExtendedOptions.cssStyles.push(...componentCSSStyles);
        } else {
          componentExtendedOptions.cssStyles.push(...componentCSSStyles);
        }

        if (!options.dependencies) {
          const parentDependencies = getComponentProperties(Object.getPrototypeOf(target)).dependencies || [];
          componentExtendedOptions.dependencies = [...parentDependencies]; // could be improved by sharing array
        }

        setComponentProperties(target, componentExtendedOptions);

        if (!isAbstract) {
          if (window.customElements.get(options.tag)) {
            console.error(`Custom element with name ${options.tag} already defined, please check your imports/bundler.`);
          } else {
            window.customElements.define(options.tag, target);
          }
        }
      }
    };
  };
}
